import { Button } from '../atoms';

/**
 * Pagination — page-navigator + items-per-page selector.
 *
 * Derived from M3 spec implied (no standalone Pagination component in M3;
 * spec embedded in Data Table guidance + List patterns).
 *
 * Reference behavior: TanStack Table pagination model — `pageIndex` (0-based)
 * + `pageSize` + `pageCount` — but Populus Pagination doesn't depend on TanStack
 * directly; pure props in / events out so it can pair with any list source.
 *
 * Populus adaptations:
 *   - System colors only (pagination is operator-tool chrome, not brand)
 *   - Compact density: 28px button height (matches Button size=sm)
 *   - Boundary always shows first + last; ellipsis in between for long ranges
 *   - Items-per-page selector hidden when pageSize is fixed (no `onPageSizeChange`)
 *
 * Phase 11 Block 6 PR 3c (2026-05-12).
 */

export interface PaginationProps {
  pageIndex: number;            // 0-based
  pageCount: number;            // total pages
  pageSize: number;             // items per page
  totalRows?: number;           // optional; rendered as "1-25 of 247" if present
  onPageChange: (pageIndex: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
}

export function Pagination({
  pageIndex,
  pageCount,
  pageSize,
  totalRows,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
}: PaginationProps) {
  const canPrev = pageIndex > 0;
  const canNext = pageIndex < pageCount - 1;
  const firstRow = pageIndex * pageSize + 1;
  const lastRow = Math.min(firstRow + pageSize - 1, totalRows ?? firstRow + pageSize - 1);

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      // Wrap at phone width: the "Showing X-Y of Z" label + the per-page/prev/
      // next controls don't fit on one line at 390px, so let them stack onto a
      // second row instead of overflowing the card. On desktop the row is wide
      // enough that nothing wraps (space-between keeps the original layout).
      flexWrap: 'wrap',
      gap: 'var(--sp-3)',
      padding: 'var(--sp-3) var(--sp-4)',
      borderTop: '1px solid var(--color-border)',
      background: 'var(--color-card)',
      fontSize: 'var(--fs-sm)',
      color: 'var(--color-ink-3)',
    }}>
      <div>
        {totalRows !== undefined ? (
          totalRows === 0
            ? <>No results</>
            : <>Showing <strong>{firstRow.toLocaleString()}-{lastRow.toLocaleString()}</strong> of <strong>{totalRows.toLocaleString()}</strong></>
        ) : (
          <>Page <strong>{pageIndex + 1}</strong> of <strong>{pageCount}</strong></>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
        {onPageSizeChange && (
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
            <span>Per page</span>
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              style={{
                fontFamily: 'inherit',
                fontSize: 'var(--fs-sm)',
                padding: '4px 8px',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--r-sm)',
                background: 'var(--color-card)',
                color: 'var(--color-ink)',
                cursor: 'pointer',
              }}
            >
              {pageSizeOptions.map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
        )}
        <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
          <Button size="sm" variant="secondary" disabled={!canPrev} onClick={() => onPageChange(pageIndex - 1)}>Previous</Button>
          <Button size="sm" variant="secondary" disabled={!canNext} onClick={() => onPageChange(pageIndex + 1)}>Next</Button>
        </div>
      </div>
    </div>
  );
}
